import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { stopAudioPlayback } from "@/shared/audio/briefing-audio";
import { getAutomaticActivitySource } from "@/shared/api/activity-source";
import { startCloudSync } from "@/shared/sync/cloud-sync";
import { runAuthBootSequence } from "./auth-boot.mjs";
import { AuthenticatedContentBoundary } from "./auth-content-boundary.mjs";
import { reduceContactVerificationGate } from "./contact-verification-gate-state.mjs";
import {
  applyAuthTokenResponse,
  captureAuthSessionOwner,
  fetchMe,
  getCurrentUser,
  isSignedIn,
  logout,
  reconcileAuthSessionFromStorage,
  CONTACT_VERIFICATION_REQUIRED_EVENT,
  CONTACT_VERIFICATION_VALIDATED_EVENT,
  restoreAuthSession,
  type AuthSessionTransitionPermit,
  type AuthTokenResponse,
  type AuthUser,
} from "./session";

type AuthContextValue = {
  user: AuthUser | null;
  signedIn: boolean;
  booting: boolean;
  contactVerificationRequired: boolean;
  setUser: (u: AuthUser | null) => void;
  signOut: () => Promise<void>;
  onTokenResponse: (tokens: AuthTokenResponse, permit: AuthSessionTransitionPermit) => boolean;
};

const AuthContext = createContext<AuthContextValue | null>(null);

function authenticatedPrincipal(user: AuthUser | null, signedIn: boolean): string {
  if (!signedIn) return "";
  return String(user?.userId || user?.id || "").trim();
}

function authenticatedSyncOwnerKey(): string {
  const owner = captureAuthSessionOwner();
  if (!owner) return "";
  return `${owner.userId.length}:${owner.userId}:${owner.sessionId}`;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const initialUser = getCurrentUser();
  const initialSignedIn = isSignedIn();
  const [user, setUser] = useState<AuthUser | null>(initialUser);
  const [signedIn, setSignedIn] = useState(initialSignedIn);
  const [booting, setBooting] = useState(true);
  const [contactVerificationRequired, setContactVerificationRequired] = useState(false);
  const [syncOwnerKey, setSyncOwnerKey] = useState(authenticatedSyncOwnerKey);
  const principalRef = useRef(authenticatedPrincipal(initialUser, initialSignedIn));
  const [contentPrincipal, setContentPrincipal] = useState(principalRef.current);

  const refreshState = useCallback(() => {
    const nextUser = getCurrentUser();
    const nextSignedIn = isSignedIn();
    const nextPrincipal = authenticatedPrincipal(nextUser, nextSignedIn);
    if (nextPrincipal !== principalRef.current) {
      // Audio and every routed component-local store belong to the old
      // principal. Stop/cancel playback before React mounts the new account.
      stopAudioPlayback();
      // Account-scoped queries deliberately use stable product keys. Reset
      // mounted observers when entering another account so their A data clears
      // synchronously and refetches for B; leaving auth can drop everything.
      if (nextPrincipal) void queryClient.resetQueries();
      else queryClient.clear();
      principalRef.current = nextPrincipal;
      setContentPrincipal(nextPrincipal);
    }
    setUser(nextUser);
    setSignedIn(nextSignedIn);
    setSyncOwnerKey(authenticatedSyncOwnerKey());
  }, [queryClient]);

  useEffect(() => {
    let cancelled = false;
    const onAuth = () => refreshState();
    const onStorage = (event: StorageEvent) => {
      if (event.key !== "tb_auth_session_v1" && event.key !== null) return;
      const stored = reconcileAuthSessionFromStorage();
      setContactVerificationRequired((current) =>
        reduceContactVerificationGate(
          current,
          stored ? "authenticated_tokens_accepted" : "session_cleared",
        ),
      );
      refreshState();
    };
    const onContactVerificationRequired = () =>
      setContactVerificationRequired((current) =>
        reduceContactVerificationGate(current, "verification_required"),
      );
    const onContactVerificationValidated = () =>
      setContactVerificationRequired((current) =>
        reduceContactVerificationGate(current, "authenticated_tokens_accepted"),
      );
    window.addEventListener("tb-auth-changed", onAuth);
    window.addEventListener("storage", onStorage);
    window.addEventListener(CONTACT_VERIFICATION_REQUIRED_EVENT, onContactVerificationRequired);
    window.addEventListener(CONTACT_VERIFICATION_VALIDATED_EVENT, onContactVerificationValidated);
    void runAuthBootSequence({
      restoreSession: restoreAuthSession,
      isSignedIn,
      refreshIdentity: (signal) =>
        fetchMe({ signal, activitySource: getAutomaticActivitySource() }),
      onReady: () => {
        if (!cancelled) {
          refreshState();
          setBooting(false);
        }
      },
    });
    return () => {
      cancelled = true;
      window.removeEventListener("tb-auth-changed", onAuth);
      window.removeEventListener("storage", onStorage);
      window.removeEventListener(
        CONTACT_VERIFICATION_REQUIRED_EVENT,
        onContactVerificationRequired,
      );
      window.removeEventListener(
        CONTACT_VERIFICATION_VALIDATED_EVENT,
        onContactVerificationValidated,
      );
    };
  }, [refreshState]);

  useEffect(() => {
    if (booting || !signedIn || contactVerificationRequired) return undefined;
    return startCloudSync();
  }, [booting, signedIn, contactVerificationRequired, syncOwnerKey]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      signedIn,
      booting,
      contactVerificationRequired,
      setUser,
      signOut: async () => {
        await logout();
        setContactVerificationRequired((current) =>
          reduceContactVerificationGate(current, "session_cleared"),
        );
        refreshState();
      },
      onTokenResponse: (tokens, permit) => {
        const session = applyAuthTokenResponse(tokens, permit);
        if (session) {
          setContactVerificationRequired((current) =>
            reduceContactVerificationGate(current, "authenticated_tokens_accepted"),
          );
        }
        refreshState();
        return Boolean(session);
      },
    }),
    [user, signedIn, booting, contactVerificationRequired, refreshState],
  );

  return (
    <AuthContext.Provider value={value}>
      <AuthenticatedContentBoundary principal={contentPrincipal}>
        {children}
      </AuthenticatedContentBoundary>
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth outside AuthProvider");
  return ctx;
}
